import { Component } from 'react'
import _ from 'lodash'
import Promise from 'bluebird'

import { Helmet } from 'react-helmet'
import PropTypes from 'prop-types'
import BeforeUnload from '../Common/BeforeUnload'
import FlowController from './FlowController'
import {
  getSpecAttributeFullField,
  getAttrTableFullField
} from '../Common/ProposalForm/Shared/util'
import {
  TOAST_DURATION,
  SKIP_REQUIRED_FIELD
} from '../../../constants/proposalTypes'
import { PROPOSAL_REVIEW_STATUS, PROPOSAL_TYPE_MAP } from '@vzmi/shp_shared_compo/constants/proposal'
import {
  pruneEmpty,
  createErrorToast,
  formRequestHandler,
  updateSyncErrorsByFormError
} from '../../../lib/FormHelper'
import withProposalListingForm from '../Common/HOC/withProposalListingForm'
import WithS3Service from '../../../containers/Common/WithS3Service'
import { HistoryUtil } from '@vzmi/component_ec_nodejs_util'
import { Toast } from '@vzmi/shp_shared_compo'
import { withSilentPageContext } from '../../../context/silentPageContext'
import { getFormData, postLoadFormat } from './helpers/getters'
import { transformFormDataToProposalData } from './helpers/convertors'
import { updateHistoryAfterProposalSaved } from '../Common/ProposalForm/Shared/historyUtil'

export class BaseForm extends Component {
  constructor (props) {
    super(props)
    this.getFormData = getFormData.bind(undefined, props)
    this.postLoadFormat = postLoadFormat.bind(undefined, props)
  }

  redirectToSCMLogin = () => {
    this.props.actions.redirectSCMLogin(500)
  }

  redirectToProposalQuery = () => {
    this.props.actions.redirectToSharedLink('scmProposalQuery')
  }

  getItemSpecs = async id => {
    const { actions } = this.props
    try {
      const proposalData = await actions
        .fetchNoCacheProposals({ id })
        .then(res => (res.error ? Promise.reject(res.payload) : res.payload))

      const firstItemId = _.get(
        proposalData,
        'product.models[0].items[0].id',
        null
      )

      if (!_.isNull(firstItemId)) {
        const models = _.get(proposalData, 'product.models', [])
        const allItemSpecs = _.flatMap(models, model => {
          const { displayName: modelSpec, items } = model

          const oneModalItemSpecsArr = items.map(item => {
            const { displayName: itemSpec, id: itemId } = item

            return {
              modelSpec,
              itemSpec,
              itemId
            }
          })
          return oneModalItemSpecsArr
        })
        return allItemSpecs
      }
      return null
    } catch (error) {
      console.log('getItemSpecs 錯誤', error)
      throw error
    }
  }

  /**
   * use 'history.replace' to redirect
   *
   * The 'history' is NOT native browser object.
   * It's from @see https://github.com/ReactTraining/history#properties
   *
   * A location-like object with { pathname, search, hash, state }
   */
  replaceHistoryTo = locationLikeObj => {
    const { history } = this.props
    history.replace(locationLikeObj)
    HistoryUtil.clearHistory()
  }

  redirectToSCMLoginWithToast = (toastMsg = '') => {
    Toast.error(toastMsg, { duration: TOAST_DURATION })
    this.redirectToSCMLogin()
  }

  loadForm = async formData => {
    this.props.initialize(formData)
  }

  handleInvalidForm = error => {
    createErrorToast({ message: error.message, error })
  }

  productListingErrorTransformer = ({
    proposalData,
    formValues
  }) => proposalError => {
    const { product = {}, subStationId } = proposalData

    const hasLevel = !_.isEmpty(product.specs)

    const priceRelatedPaths = ['product.msrp', 'product.cost', 'listing.price']
    const nextProposalError = {}
    const tempGroupByCodeError = {}
    const attributeErrors = {}
    const modelsErrors = {}

    const attributeRegexp = /^(product\.attributes\[\d+\])/
    const modelsRegexp = /^(product\.models\[\d+\])/
    _.map(proposalError, (value, key) => {
      const attributeKey = _.get(attributeRegexp.exec(key), '[0]')
      const isProductModelsKey = modelsRegexp.test(key)

      if (attributeKey) {
        const attributeData = _.get(proposalData, attributeKey)
        // 處理規格表錯誤，轉換錯誤至對應transient spec attributes 欄位
        // @example __transient__.attribute.sub28_catItem17170_000003326581.attributes.withoutLevel
        const transientSpecAttributesKey = getSpecAttributeFullField(
          subStationId,
          _.get(product, 'catItemId'),
          _.get(product, 'struDataAttrClusterId'),
          hasLevel
        )
        const formSpecAttributes = _.get(formValues, transientSpecAttributesKey)
        const transientAttributePosIndex = _.findIndex(formSpecAttributes, {
          name: attributeData.name
        })
        const nextPath = `${transientSpecAttributesKey}[${transientAttributePosIndex}].values`
        attributeErrors[nextPath] = value
      } else if (isProductModelsKey) {
        const transientModelPath = key.replace('product.', '')
        // 處理 product.models[] 錯誤，轉換錯誤至對應transient spec table 欄位
        // @example __transient__.attribute.sub28_catItem17170_000003326581.table.withoutLevel
        const transientSpecTableKey = getAttrTableFullField(
          subStationId,
          _.get(product, 'catItemId'),
          _.get(product, 'struDataAttrClusterId'),
          hasLevel
        )
        const fieldPath = `${transientSpecTableKey}.${transientModelPath}`
        modelsErrors[fieldPath] = value
      } else {
        // 轉換市價，售價，成本API錯誤，將其重複的只顯示於第一個欄位上
        // 若非這三個欄位則正常儲存錯誤
        if (_.includes(priceRelatedPaths, key)) {
          _.map(value, e => {
            const { code } = e
            // 用錯誤碼攤平錯誤物件
            if (!_.has(tempGroupByCodeError, code)) {
              tempGroupByCodeError[code] = e
            }
          })
        } else {
          nextProposalError[key] = value
        }
      }
    })

    // 重新將市價，售價，成本API錯誤分組
    const productPriceRelatedErrors = _.groupBy(tempGroupByCodeError, 'path')

    const finalSyncErrors = _.merge(
      {},
      nextProposalError,
      attributeErrors,
      modelsErrors,
      productPriceRelatedErrors
    )
    return finalSyncErrors
  }

  onSubmissionFinally = () => {
    // API 提交成功/失敗，`SKIP_REQUIRED_FIELD` 改 false 避免表單沒有檢查到必填欄位
    // 因為修改SKIP_REQUIRED_FIELD欄位會觸發必填欄位驗證，所以必須清除Error
    this.props.change(SKIP_REQUIRED_FIELD, false)
    this.props.clearSubmitErrors()
  }

  /**
   * API 送出 Flow:
   *
   * => 送出提案
   * => API若出錯
   * => 拋出表單錯誤Error
   * => promise.finally 表單改回必填，並清除預設必填錯誤
   * => 處理API錯誤並更新至表單
   *
   */
  saveForm = async (proposalData, formValues) => {
    const {
      updateFormSyncErrors,
      actions: { createProposals, updateProposals }
    } = this.props

    // 如果有提案編號則更新提案，否則新增提案
    const submitProposal = proposalData.id ? updateProposals : createProposals

    const prunedProposalData = pruneEmpty(proposalData)
    const proposalWithoutInvalidFields = _.omit(
      prunedProposalData,
      [
        // executedTs 只用來顯示在已執行提案的檢視頁
        // DEFAULT_FORM_DATA 會用來指定 post / put proposal 預設欄位
        // 避免踩到 post proposal 禁止含有 executedTs 欄位，所以抽出來另外放
        // https://jira.ouryahoo.com/browse/APACSMDESK-60751
        'executedTs'
      ]
    )
    const submissionPromise = formRequestHandler(submitProposal(proposalWithoutInvalidFields))

    return submissionPromise
      .finally(this.onSubmissionFinally)
      .catch(formError => {
        console.error('提案失敗', formError)
        updateSyncErrorsByFormError({
          transformer: this.productListingErrorTransformer({
            proposalData,
            formValues
          }),
          formError,
          updateFormSyncErrors
        })

        throw formError
      })
  }

  preSaveFormat = async formValues => {
    return transformFormDataToProposalData(formValues)
  }

  // 儲存暫不提案
  saveWithoutSubmit = async (formValues, dispatch, props) => {
    try {
      let formData = await this.preSaveFormat(formValues)
      formData = _.merge({}, formData, {
        reviewStatus: PROPOSAL_REVIEW_STATUS.DRAFT
      })
      const returnFormData = await this.saveForm(formData, formValues)
      Toast.success(
        <div style={{ width: 220, textAlign: 'left' }}>
          賣場已儲存，尚未提案
        </div>,
        { duration: TOAST_DURATION }
      )
      this.redirectToProposalQuery()
      return returnFormData
    } catch (error) {
      console.error('儲存暫不提案失敗', error)
      throw error
    }
  }

  // 送出提案
  pendingReviewSubmit = async (formValues, dispatch, props) => {
    try {
      let formData = await this.preSaveFormat(formValues)
      formData = _.merge({}, formData, {
        reviewStatus: PROPOSAL_REVIEW_STATUS.PENDING_REVIEW
      })
      const returnFormData = await this.saveForm(formData, formValues)
      return returnFormData
    } catch (error) {
      console.error('送出提案失敗', error)
      throw error
    }
  }

  // 儲存草稿
  // 存草稿前， `SKIP_REQUIRED_FIELD` 是 false,
  // 按下存草稿， `SKIP_REQUIRED_FIELD` 改 true，

  // API 提交成功，資料倒回來後， `SKIP_REQUIRED_FIELD` 改 false
  // API 提交失敗，`SKIP_REQUIRED_FIELD` 改 false
  savingDraftSubmit = async (formValues, dispatch, props) => {
    try {
      let formData = await this.preSaveFormat(formValues)
      formData = _.merge({}, formData, { reviewStatus: PROPOSAL_REVIEW_STATUS.COMPOSING })
      const returnFormData = await this.saveForm(formData, formValues)
      const finalFormData = await this.postLoadFormat(
        formValues,
        returnFormData
      )
      updateHistoryAfterProposalSaved(props, returnFormData)
      // 以回傳資料重設表單
      this.loadForm(finalFormData)
      return finalFormData
    } catch (error) {
      console.error('儲存草稿失敗', error)
      throw error
    }
  }

  // 完成提案
  submitForm = async (formValues, dispatch, props) => {
    try {
      let formData = await this.preSaveFormat(formValues)
      formData = _.merge({}, formData, {
        reviewStatus: PROPOSAL_REVIEW_STATUS.DRAFT
      })
      const returnFormData = await this.saveForm(formData, formValues)
      const finalFormData = await this.postLoadFormat(
        formValues,
        returnFormData
      )
      updateHistoryAfterProposalSaved(props, returnFormData)
      // 以回傳資料重設表單
      this.loadForm(finalFormData)
      return finalFormData
    } catch (error) {
      console.error('完成提案', error)
      throw error
    }
  }

  render () {
    const { subStationOptions, proposalType } = this.props
    const title = `新增${PROPOSAL_TYPE_MAP[proposalType]}提案`
    return (
      <BeforeUnload ref={r => (this.beforeunload = r)}>
        <Helmet key='helmet'>
          <title>{title}</title>
        </Helmet>
        <FlowController
          key='form'
          {...this.props}
          ref={e => (this.composeForm = e)}
          loadForm={this.loadForm}
          postLoadFormat={this.postLoadFormat}
          preSaveFormat={this.preSaveFormat}
          getFormData={this.getFormData}
          getItemSpecs={this.getItemSpecs}
          onFormSubmit={this.submitForm}
          onSavingDraftSubmit={this.savingDraftSubmit}
          onSaveWithoutSubmit={this.saveWithoutSubmit}
          onPendingReviewSubmit={this.pendingReviewSubmit}
          subStationOptions={subStationOptions}
        />
      </BeforeUnload>
    )
  }
}

BaseForm.propTypes = {
  user: PropTypes.object.isRequired,
  setting: PropTypes.object.isRequired,
  proposalType: PropTypes.string.isRequired,
  subStationOptions: PropTypes.array.isRequired,
  actions: PropTypes.shape({
    fetchRecentlyCategoryOptions: PropTypes.func.isRequired,
    fetchCategoryOptions: PropTypes.func.isRequired,
    fetchStruDataAttrClusterOptions: PropTypes.func.isRequired,
    fetchProposals: PropTypes.func.isRequired,
    createProposals: PropTypes.func.isRequired,
    updateProposals: PropTypes.func.isRequired
  })
}

const hoc = _.flow([withProposalListingForm, withSilentPageContext])
const ProductListingBaseForm = WithS3Service(hoc(BaseForm))

export default ProductListingBaseForm
